/**
 * NIHARA BLOG — blog.js
 * Luxury editorial blog listing page interactions
 * Vanilla ES6+, zero dependencies
 */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* =========================================================================
     1. Scroll Reveal Animations
     ========================================================================= */
  function initReveal() {
    if (prefersReducedMotion) {
      document.querySelectorAll('.nb-reveal').forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -60px 0px' });

    document.querySelectorAll('.nb-reveal').forEach(el => observer.observe(el));
  }

  /* =========================================================================
     2. Category + Tag Filtering
     ========================================================================= */
  function initFilters() {
    const catBtns  = document.querySelectorAll('[data-filter-cat]');
    const tagBtns  = document.querySelectorAll('.nb-tag[data-filter-tag]');
    const cards    = document.querySelectorAll('.nb-article-card[data-category]');
    const countEl  = document.querySelector('[data-article-count] strong');
    const noRes    = document.querySelector('.nb-no-results');

    if (!catBtns.length && !tagBtns.length) return;

    let activeCategory = 'all';
    let activeTags = new Set();

    function matchesFilters(card) {
      const cardCat = (card.getAttribute('data-category') || '').toLowerCase().replace(/\s+/g, '-');
      const cardTagsRaw = card.getAttribute('data-tags') || '';
      const cardTags = cardTagsRaw.split(',').map(t => t.trim().toLowerCase());

      const catOk = activeCategory === 'all' || cardCat === activeCategory;
      const tagOk = activeTags.size === 0 || [...activeTags].every(t => cardTags.includes(t));
      return catOk && tagOk;
    }

    function applyFilters() {
      let visibleCount = 0;
      cards.forEach(card => {
        const show = matchesFilters(card);
        card.style.display = show ? '' : 'none';
        if (show) visibleCount++;
      });
      if (countEl) countEl.textContent = visibleCount;
      if (noRes) noRes.classList.toggle('is-visible', visibleCount === 0);
    }

    catBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        catBtns.forEach(b => { b.classList.remove('is-active'); b.setAttribute('aria-pressed', 'false'); });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        activeCategory = btn.getAttribute('data-filter-cat');
        applyFilters();
      });
    });

    tagBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const tag = btn.getAttribute('data-filter-tag').toLowerCase();
        if (activeTags.has(tag)) {
          activeTags.delete(tag);
          btn.classList.remove('is-active');
        } else {
          activeTags.add(tag);
          btn.classList.add('is-active');
        }
        applyFilters();
      });
    });
  }

  /* =========================================================================
     3. Search (live filter)
     ========================================================================= */
  function initSearch() {
    const searchInputs = document.querySelectorAll('.nb-blog-hero__search-input, [data-blog-search]');
    const cards = document.querySelectorAll('.nb-article-card');
    const countEl = document.querySelector('[data-article-count] strong');
    const noRes = document.querySelector('.nb-no-results');

    if (!searchInputs.length) return;

    let debounceTimer;

    function doSearch(query) {
      const q = query.trim().toLowerCase();
      let count = 0;

      cards.forEach(card => {
        if (card.style.display === 'none' && !card.classList.contains('nb-search-hidden')) return;
        const title    = (card.getAttribute('data-title') || '').toLowerCase();
        const excerpt  = (card.getAttribute('data-excerpt') || '').toLowerCase();
        const tags     = (card.getAttribute('data-tags') || '').toLowerCase();
        const category = (card.getAttribute('data-category') || '').toLowerCase();
        const match    = !q || title.includes(q) || excerpt.includes(q) || tags.includes(q) || category.includes(q);

        if (match) {
          card.classList.remove('nb-search-hidden');
          card.style.display = '';
          count++;
        } else {
          card.classList.add('nb-search-hidden');
          card.style.display = 'none';
        }
      });

      if (countEl) countEl.textContent = count;
      if (noRes) noRes.classList.toggle('is-visible', count === 0);
    }

    searchInputs.forEach(input => {
      input.addEventListener('input', (e) => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => doSearch(e.target.value), 280);
      });
    });

    // Handle search form submit for routing
    document.querySelectorAll('.nb-blog-hero__search').forEach(form => {
      form.addEventListener('submit', (e) => {
        const input = form.querySelector('input[type="search"]');
        if (input && input.value.trim()) {
          // Allow native form submission for Shopify search routing
          return true;
        }
        e.preventDefault();
      });
    });
  }

  /* =========================================================================
     4. Sort
     ========================================================================= */
  function initSort() {
    const sortSelect = document.querySelector('[data-v-sort-select], .nb-toolbar__sort');
    if (!sortSelect) return;

    sortSelect.addEventListener('change', (e) => {
      const val = e.target.value;
      const grids = document.querySelectorAll('.nb-article-grid, .nb-featured-layout__secondary');

      grids.forEach(grid => {
        const cards = Array.from(grid.querySelectorAll('.nb-article-card'));
        cards.sort((a, b) => {
          const dateA = parseInt(a.getAttribute('data-date') || '0');
          const dateB = parseInt(b.getAttribute('data-date') || '0');
          const titleA = (a.getAttribute('data-title') || '').toLowerCase();
          const titleB = (b.getAttribute('data-title') || '').toLowerCase();

          switch (val) {
            case 'newest':      return dateB - dateA;
            case 'oldest':      return dateA - dateB;
            case 'title-asc':   return titleA.localeCompare(titleB);
            case 'title-desc':  return titleB.localeCompare(titleA);
            default:            return 0;
          }
        });

        cards.forEach(card => grid.appendChild(card));
      });
    });
  }

  /* =========================================================================
     5. Grid / List Toggle
     ========================================================================= */
  function initViewToggle() {
    const toggleBtns = document.querySelectorAll('.nb-toolbar__view-btn');
    if (!toggleBtns.length) return;

    const grids = document.querySelectorAll('.nb-article-grid');
    const savedView = localStorage.getItem('nb-view-mode') || 'grid';

    function setView(view) {
      grids.forEach(grid => grid.classList.toggle('nb-article-grid--list', view === 'list'));
      toggleBtns.forEach(btn => {
        const isActive = btn.getAttribute('data-view') === view;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', String(isActive));
      });
      localStorage.setItem('nb-view-mode', view);
    }

    setView(savedView);

    toggleBtns.forEach(btn => {
      btn.addEventListener('click', () => setView(btn.getAttribute('data-view')));
    });
  }

  /* =========================================================================
     6. Load More
     ========================================================================= */
  function initLoadMore() {
    const btn = document.querySelector('[data-nb-load-more]');
    if (!btn) return;

    const grid = document.querySelector('[data-nb-grid]');
    if (!grid) return;

    const batchSize = parseInt(grid.getAttribute('data-batch-size') || '6');
    const hiddenCards = () => Array.from(grid.querySelectorAll('.nb-article-card[data-nb-hidden]'));
    const counterEl = document.querySelector('[data-nb-remaining]');

    function updateBtn() {
      const remaining = hiddenCards().length;
      if (remaining <= 0) {
        btn.closest('.nb-load-more-wrap')?.style.setProperty('display', 'none');
      }
      if (counterEl) counterEl.textContent = remaining;
    }

    btn.addEventListener('click', () => {
      const toShow = hiddenCards().slice(0, batchSize);
      toShow.forEach((card, i) => {
        card.removeAttribute('data-nb-hidden');
        card.style.display = '';
        if (!prefersReducedMotion) {
          card.style.opacity = '0';
          card.style.transform = 'translateY(20px)';
          setTimeout(() => {
            card.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
            card.style.opacity = '1';
            card.style.transform = '';
          }, i * 80);
        }
      });
      updateBtn();
    });

    updateBtn();
  }

  /* =========================================================================
     7. Newsletter form
     ========================================================================= */
  function initNewsletter() {
    const forms = document.querySelectorAll('[data-newsletter-form]');
    forms.forEach(form => {
      form.addEventListener('submit', (e) => {
        const emailInput = form.querySelector('input[type="email"]');
        if (!emailInput) return;

        const email = emailInput.value.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(email)) {
          e.preventDefault();
          emailInput.style.borderColor = '#e85d5d';
          setTimeout(() => emailInput.style.borderColor = '', 2000);
          return;
        }

        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'Subscribing...';
        }
      });
    });
  }

  /* =========================================================================
     8. Back to Top
     ========================================================================= */
  function initBackToTop() {
    const btn = document.querySelector('.nb-back-to-top');
    if (!btn) return;

    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          btn.classList.toggle('is-visible', window.scrollY > 600);
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    });
  }

  /* =========================================================================
     9. Infinite Scroll (opt-in via data-infinite-scroll)
     ========================================================================= */
  function initInfiniteScroll() {
    const section = document.querySelector('[data-infinite-scroll]');
    if (!section) return;

    const sentinel = section.querySelector('[data-scroll-sentinel]');
    const grid = section.querySelector('[data-nb-grid]');
    if (!sentinel || !grid) return;

    let nextUrl = section.getAttribute('data-next-url');
    let loading = false;

    if (!nextUrl) return;

    const observer = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting || loading || !nextUrl) return;
      loading = true;
      sentinel.setAttribute('aria-busy', 'true');

      try {
        const res = await fetch(nextUrl);
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newCards = doc.querySelectorAll('.nb-article-card');
        const newNext = doc.querySelector('[data-next-url]')?.getAttribute('data-next-url');

        newCards.forEach(card => {
          card.classList.add('nb-reveal');
          grid.appendChild(card);
          // Trigger reveal after append
          requestAnimationFrame(() => card.classList.add('is-visible'));
        });

        nextUrl = newNext || null;
        if (!nextUrl) observer.disconnect();
      } catch (err) {
        console.warn('Nihara Blog: infinite scroll fetch failed', err);
      } finally {
        loading = false;
        sentinel.removeAttribute('aria-busy');
      }
    }, { rootMargin: '200px' });

    observer.observe(sentinel);
  }

  /* =========================================================================
     10. Sticky category tabs shadow
     ========================================================================= */
  function initStickyTabs() {
    const tabs = document.querySelector('.nb-category-tabs-section');
    if (!tabs) return;

    const observer = new IntersectionObserver(([entry]) => {
      tabs.classList.toggle('is-stuck', !entry.isIntersecting);
    }, { threshold: 1, rootMargin: '-1px 0px 0px 0px' });

    observer.observe(tabs);
  }

  /* =========================================================================
     Init
     ========================================================================= */
  function init() {
    initReveal();
    initFilters();
    initSearch();
    initSort();
    initViewToggle();
    initLoadMore();
    initNewsletter();
    initBackToTop();
    initInfiniteScroll();
    initStickyTabs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor live reload
  document.addEventListener('shopify:section:load', init);

})();
